import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { api, NETWORKS, ASSETS, STRATEGY_TAGS, WHY_BUY, fmtUsd, fixed } from "@/lib/journal";
import { WhyPicker } from "@/components/WhyPicker";
import { CloudArrowDown, CircleNotch } from "@phosphor-icons/react";

const localNow = () => {
  const d = new Date(Date.now() - new Date().getTimezoneOffset() * 60000);
  return d.toISOString().slice(0, 16);
};

const empty = {
  network: "ethereum",
  entry_tx_hash: "",
  asset: "WETH",
  volume: "",
  entry_price: "",
  why_buy: "",
  reason: "",
  tags: [],
  status: "open",
  entry_date: localNow(),
};

export const BuyDialog = ({ open, onOpenChange, onSaved, portfolios = [], defaultPortfolioId }) => {
  const { t } = useTranslation();
  const [f, setF] = useState(empty);
  const [portfolioId, setPortfolioId] = useState(defaultPortfolioId);
  const [walletAddress, setWalletAddress] = useState("");

  const activePortfolio = portfolios.find((x) => x.id === portfolioId);
  const isOnchain = (activePortfolio?.kind || "onchain") === "onchain";

  useEffect(() => {
    const p = portfolios.find((x) => x.id === portfolioId);
    setWalletAddress(p?.address || "");
    // Brokers and exchanges have no default ticker — WETH only makes sense on-chain.
    if (p && p.kind && p.kind !== "onchain") {
      setF((prev) => (prev.asset === "WETH" ? { ...prev, asset: "" } : prev));
    } else if (p) {
      setF((prev) => (prev.asset === "" ? { ...prev, asset: "WETH" } : prev));
    }
  }, [portfolioId, portfolios]);

  useEffect(() => { setPortfolioId(defaultPortfolioId); }, [defaultPortfolioId]);
  const [fetching, setFetching] = useState(false);
  const [pricing, setPricing] = useState(false);

  const tradeIsToday = (f.entry_date || "").slice(0, 10) === localNow().slice(0, 10);

  const useMarketPrice = async () => {
    if (!tradeIsToday) return toast.error(t("buy.marketPriceTodayOnly"));
    setPricing(true);
    try {
      const { data } = await api.get("/prices", { params: { symbols: f.asset.toUpperCase() } });
      const p = data.prices?.[f.asset.toUpperCase()];
      if (!p) return toast.error(`No market price found for ${f.asset}`);
      set("entry_price", String(p));
      toast.success(`${f.asset} market price loaded`);
    } catch {
      toast.error("Failed to load market price");
    } finally {
      setPricing(false);
    }
  };
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const toggleTag = (t) =>
    setF((p) => ({ ...p, tags: p.tags.includes(t) ? p.tags.filter((x) => x !== t) : [...p.tags, t] }));

  const fetchTx = async () => {
    if (!f.entry_tx_hash.trim()) return toast.error("Enter a transaction hash first");
    setFetching(true);
    try {
      const { data } = await api.post("/tx/fetch", {
        tx_hash: f.entry_tx_hash.trim(),
        network: f.network,
        wallet: walletAddress || null,
      });
      setF((p) => ({
        ...p,
        network: data.network || p.network,
        asset: data.asset || p.asset,
        volume: data.volume != null ? String(Number(fixed(data.volume, 8, "0"))) : p.volume,
        entry_price: data.entry_price != null ? String(data.entry_price) : p.entry_price,
        entry_date: data.date || undefined,
      }));
      toast.success(
        data.asset
          ? `Parsed ${data.asset} swap on ${data.network}${data.spent_symbol ? ` paid in ${data.spent_symbol}` : ""}`
          : data.note || "Transaction found but asset could not be identified — please fill in manually"
      );
      if (data.asset && data.note) toast.info(data.note);
      if (data.wallet && !walletAddress) setWalletAddress(data.wallet);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to fetch transaction");
    } finally {
      setFetching(false);
    }
  };

  const submit = async () => {
    if (!f.asset || !f.volume)
      return toast.error("Asset and volume are required");
    if (!isOnchain && f.entry_price === "")
      return toast.error(t("buy.priceRequired"));
    if (!f.why_buy) return toast.error("Pick why you are buying — it powers the Human Factor stats");
    setSaving(true);
    try {
      await api.post("/trades", {
        ...f,
        portfolio_id: portfolioId === "all" ? null : portfolioId,
        volume: parseFloat(f.volume),
        entry_date: f.entry_date ? new Date(f.entry_date).toISOString() : null,
        entry_price: f.entry_price === "" ? null : parseFloat(f.entry_price),
        entry_tx_hash: f.entry_tx_hash.trim() || null,
      });
      toast.success(f.entry_price === "" ? "Trade logged at market price" : "Trade logged");
      setF(empty);
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save trade");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="buy-dialog"
        className="max-h-[90vh] overflow-y-auto rounded-none border-[#1F1F1F] bg-[#0A0A0A] sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium tracking-tight">
            New Entry <span className="text-[#C58361]">/ Buy</span>
          </DialogTitle>
          <DialogDescription className="text-sm text-zinc-500">
            Log a spot or wrapped-pair purchase. Fetch details from chain or enter them manually.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Portfolio</Label>
            <Select value={portfolioId} onValueChange={setPortfolioId}>
              <SelectTrigger data-testid="buy-portfolio-select" className="rounded-none border-[#1F1F1F] bg-[#050505]">
                <SelectValue placeholder="Select portfolio" />
              </SelectTrigger>
              <SelectContent className="rounded-none border-[#1F1F1F] bg-[#0A0A0A]">
                {portfolios.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`buy-portfolio-${p.id}`}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isOnchain && (<>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Network</Label>
            <Select value={f.network} onValueChange={(v) => set("network", v)}>
              <SelectTrigger data-testid="buy-network-select" className="rounded-none border-[#1F1F1F] bg-[#050505]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none border-[#1F1F1F] bg-[#0A0A0A]">
                {NETWORKS.map((n) => (
                  <SelectItem key={n.id} value={n.id} data-testid={`buy-network-${n.id}`}>{n.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Entry Tx Hash</Label>
            <div className="flex gap-2">
              <Input
                data-testid="buy-txhash-input"
                value={f.entry_tx_hash}
                onChange={(e) => set("entry_tx_hash", e.target.value)}
                placeholder="0x…"
                className="rounded-none border-[#1F1F1F] bg-[#050505] font-mono text-xs focus-visible:ring-[#C58361]"
              />
              <Button
                data-testid="fetch-blockchain-btn"
                onClick={fetchTx}
                disabled={fetching}
                className="shrink-0 rounded-none bg-[#C58361] font-medium text-black hover:bg-[#A86B4C]"
              >
                {fetching ? <CircleNotch size={16} className="animate-spin" /> : <CloudArrowDown size={16} />}
                <span className="ml-2 hidden sm:inline">Fetch</span>
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">
              Your Wallet <span className="text-zinc-600">— used to read the swap</span>
            </Label>
            <Input
              data-testid="buy-wallet-input"
              value={walletAddress}
              onChange={(e) => setWalletAddress(e.target.value)}
              placeholder="0x… (optional, improves parsing of router swaps)"
              className="rounded-none border-[#1F1F1F] bg-[#050505] font-mono text-xs focus-visible:ring-[#C58361]"
            />
          </div>
          </>)}

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">{t("buy.tradeDate")}</Label>
            <Input
              data-testid="buy-date-input"
              type="datetime-local"
              value={f.entry_date}
              onChange={(e) => set("entry_date", e.target.value)}
              className="rounded-none border-[#1F1F1F] bg-[#050505] font-mono text-xs focus-visible:ring-[#C58361]"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Asset</Label>
              <Input
                data-testid="buy-asset-input"
                list="asset-list"
                value={f.asset}
                placeholder={isOnchain ? undefined : t("buy.assetPlaceholder")}
                onChange={(e) => set("asset", e.target.value.trim())}
                className="rounded-none border-[#1F1F1F] bg-[#050505] font-mono focus-visible:ring-[#C58361]"
              />
              <datalist id="asset-list">
                {ASSETS.map((a) => <option key={a} value={a} />)}
              </datalist>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">{t("buy.volume")}</Label>
              <Input
                data-testid="buy-volume-input"
                type="number" step="any" value={f.volume}
                onChange={(e) => set("volume", e.target.value)}
                placeholder="0.00"
                className="rounded-none border-[#1F1F1F] bg-[#050505] font-mono focus-visible:ring-[#C58361]"
              />
              <p className="font-mono text-[10px] text-zinc-600">{t("buy.volumeHint")}</p>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">
                Entry Price (USD){" "}
                {isOnchain ? (
                  <span className="text-zinc-600">— optional</span>
                ) : (
                  <span className="text-[#C58361]">*</span>
                )}
              </Label>
              <button
                type="button"
                data-testid="buy-market-price-btn"
                onClick={useMarketPrice}
                disabled={pricing || !f.asset || !tradeIsToday}
                title={tradeIsToday ? undefined : t("buy.marketPriceTodayOnly")}
                className="font-mono text-[10px] uppercase tracking-widest text-[#C58361] transition-opacity hover:opacity-70 disabled:opacity-30"
              >
                {pricing ? "Loading…" : "Use market price"}
              </button>
            </div>
            <Input
              data-testid="buy-price-input"
              type="number" step="any" value={f.entry_price}
              onChange={(e) => set("entry_price", e.target.value)}
              placeholder={isOnchain ? "Leave blank to use the current market price" : t("buy.pricePlaceholder")}
              className="rounded-none border-[#1F1F1F] bg-[#050505] font-mono focus-visible:ring-[#C58361]"
            />
            {f.volume && f.entry_price ? (
              <p className="font-mono text-xs text-zinc-500">
                Cost basis {fmtUsd(parseFloat(f.volume) * parseFloat(f.entry_price) || 0)}
              </p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">
              Why Buy? <span className="text-[#C58361]">*</span>
            </Label>
            <WhyPicker
              options={WHY_BUY}
              value={f.why_buy}
              onChange={(v) => set("why_buy", v)}
              testPrefix="buy-why"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Reason for Purchase</Label>
            <Textarea
              data-testid="buy-reason-input"
              value={f.reason}
              onChange={(e) => set("reason", e.target.value)}
              rows={3}
              placeholder="Thesis, catalyst, invalidation level…"
              className="rounded-none border-[#1F1F1F] bg-[#050505] focus-visible:ring-[#C58361]"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Strategy Tags</Label>
            <div className="flex flex-wrap gap-2">
              {STRATEGY_TAGS.map((t) => (
                <button
                  key={t}
                  type="button"
                  data-testid={`buy-tag-${t.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => toggleTag(t)}
                  className={`border px-2.5 py-1 text-xs transition-colors duration-200 ${
                    f.tags.includes(t)
                      ? "border-[#C58361] bg-[#C58361]/10 text-[#C58361]"
                      : "border-[#1F1F1F] text-zinc-500 hover:text-zinc-200"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Status</Label>
            <Select value={f.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="buy-status-select" className="rounded-none border-[#1F1F1F] bg-[#050505]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none border-[#1F1F1F] bg-[#0A0A0A]">
                <SelectItem value="open" data-testid="buy-status-open">Open</SelectItem>
                <SelectItem value="closed" data-testid="buy-status-closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button
            data-testid="buy-submit-btn"
            onClick={submit}
            disabled={saving || !f.why_buy}
            className="w-full rounded-none bg-[#C58361] py-6 font-medium text-black hover:bg-[#A86B4C]"
          >
            {saving ? "Saving…" : "Log Trade"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
